import { Button, Card, Col, Form, InputNumber, message, Row, Select, Space } from "antd";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "common/routes";
import { createPurchaseIndent, resetPurchaseIndentStatus } from "store/purchase-indent/purchaseIndentSlice";
import { getActivePurchaseCodes } from "store/vendor/vendorSlice";

const MESSAGE_KEY = "create-purchase-indent";

interface CreateIndentFormValues {
  purchaseSkuCode: string;
  qty: number;
}

const PurchaseIndentCreatePage = () => {
  useDocumentTitle("Create Purchase Indent");
  const [form] = Form.useForm<CreateIndentFormValues>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { status, error } = useAppSelector((state) => state.purchaseIndent);
  const { activePurchaseCodes, status: vendorStatus } = useAppSelector((state) => state.vendors);

  useEffect(() => {
    dispatch(resetPurchaseIndentStatus());
    dispatch(getActivePurchaseCodes());
  }, [dispatch]);

  useEffect(() => {
    if (status === "saving") {
      message.loading({ content: "Creating purchase indent...", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error || "Something went wrong!", key: MESSAGE_KEY });
    }
    return () => {
      message.destroy(MESSAGE_KEY);
    };
  }, [status, error]);

  const onFinish = (values: CreateIndentFormValues) => {
    dispatch(createPurchaseIndent(values))
      .unwrap()
      .then(() => {
        message.success({ content: "Purchase indent created!", key: MESSAGE_KEY });
        navigate(`/${routes.PURCHASE_INDENT.ALL}`);
      });
  };

  const skuOptions = activePurchaseCodes.map((sku) => ({
    value: sku.skuCode,
    label: `${sku.name} (${sku.skuCode})`,
  }));

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader
            title="Create Purchase Indent"
            onBack={() => navigate(`/${routes.PURCHASE_INDENT.ALL}`)}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={24} sm={20} md={14} lg={10} xl={8}>
          <Card>
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              requiredMark={false}
            >
              <Form.Item
                name="purchaseSkuCode"
                label="Purchase SKU"
                rules={[{ required: true, message: "Please select a purchase SKU" }]}
              >
                <Select
                  placeholder="Select purchase SKU"
                  showSearch
                  allowClear
                  loading={vendorStatus === "loading"}
                  optionFilterProp="label"
                  options={skuOptions}
                />
              </Form.Item>

              <Form.Item
                name="qty"
                label="Quantity"
                rules={[
                  { required: true, message: "Please enter a quantity" },
                  {
                    validator: (_, value) =>
                      value > 0
                        ? Promise.resolve()
                        : Promise.reject(new Error("Quantity must be greater than 0")),
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter quantity"
                  precision={2}
                  min={0.01}
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Space size="middle">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={status === "saving"}
                  >
                    Create Indent
                  </Button>
                  <Button onClick={() => navigate(`/${routes.PURCHASE_INDENT.ALL}`)}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PurchaseIndentCreatePage;
